import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionPricing from '../../_Sections/SectionPricing';
import SectionSizes from '../../_Sections/SectionSizes';
import SectionGallery from '../../_Sections/SectionGallery';
import icon1Url from '@resources/img/pages/products/printed-patches/icon-1.svg?url';
import icon2Url from '@resources/img/pages/products/printed-patches/icon-2.svg?url';
import icon3Url from '@resources/img/pages/products/printed-patches/icon-3.svg?url';
import icon4Url from '@resources/img/pages/products/printed-patches/icon-4.svg?url';
import icon5Url from '@resources/img/pages/products/printed-patches/icon-5.svg?url';
import PrintedPatchMeasurementDiagram from './PrintedPatchMeasurementDiagram';

const PRODUCT_ID = 138;
const VIEW_PRODUCT_ID = 168;

const bannerImages = {
  en_CA: new URL('@resources/img/pages/products/printed-patches/banner-1-en_CA.png', import.meta.url).href,
  fr_CA: new URL('@resources/img/pages/products/printed-patches/banner-1-fr_CA.png', import.meta.url).href,
};

const galleryImages = [
  new URL('@resources/img/pages/products/printed-patches/gallery_0.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/printed-patches/gallery_1.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/printed-patches/gallery_2.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/printed-patches/gallery_3.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/printed-patches/gallery_4.jpg', import.meta.url).href,
];

export default function PrintedPatchPage() {
  const [t] = useTranslation('page/products/printed-patches');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const bannerImage = locale === 'fr_CA' ? bannerImages.fr_CA : bannerImages.en_CA;

  if (!productData) return null;

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom Printed Patch | Products')}
      description={t('Print-on-demand custom patch made from waterproof PVC with vibrant full-colour print, edge stitching and iron-on backing.')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#abebb9"
      bgStyle={{
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '50%',
      }}
      tag={t('The all-over print')}
      title={t('Printed Patch')}
      text={t('Turn your artwork into a custom patch that is as fun as it is durable! Printed in vibrant full colour on demand, this waterproof PVC patch makes it easy to add personality to jackets, backpacks, hats, and more. Each patch is finished with edge stitching and iron-on backing for easy application.')}
      createUrl={productData.get('url_slug')}
      img={bannerImage}
      alt={t('Picture of Printed Patch')}
      details={[
        {
          img: icon1Url,
          alt: t('PVC icon'),
          text: t('Made from durable, waterproof PVC'),
        },
        {
          img: icon2Url,
          alt: t('Edge stitching icon'),
          text: t('Finished with edge stitching for a polished look'),
        },
        {
          img: icon3Url,
          alt: t('Iron-on backing icon'),
          text: t('Features a soft sueded back with iron-on adhesive'),
        },
        {
          img: icon4Url,
          alt: t('Full colour printing icon'),
          text: t('Printed in vibrant full colour with exceptional durability'),
        },
        {
          img: icon5Url,
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />

    <SectionGallery images={galleryImages} />

    <SectionPricing
      productId={PRODUCT_ID}
      variants={['circle_2_5', 'oval_3_5', 'rectangle_3_5', 'square_2_5']}
      listOptions
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Printed Patch Sizing')}
      text={t('Available in 4 shapes, each offered in 2 sizes. Please allow for some variance in the placement of your design within an order. Each patch is handled individually by staff, so there may be some differences in image placement.')}
    >
      <PrintedPatchMeasurementDiagram ariaLabel={t('Printed Patch shapes and sizes')} />
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Printed Patch')}
      text={t('Branding enhances your products. Add custom artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Printed Patch with these great products to create your own unique styles.')}
      productSlugs={['basic-tote', 'premium-drawstring-bag', 'makeup-bags']}
    />
  </div>
}
